import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UPLOAD_BURST_CONCURRENCY } from "../hooks/useReportUpload";

const UploadJobsContext = createContext(null);

function countQueuedFiles(queue) {
  return queue.reduce((sum, job) => sum + (job.files.length - job.idx), 0);
}

/**
 * Background FIFO upload queue: each job is { reportId, files, idx }.
 * Files upload one-by-one; new jobs enqueue behind the current one.
 */
export function UploadJobsProvider({
  children,
  uploadFileAsync,
  pollAttachmentStatus,
  setErrors,
  setShowToast,
  onPendingCountChange,
  onRegisterClearQueue,
  onRegisterEnqueue,
}) {
  const queueRef = useRef([]);
  const drainingRef = useRef(false);
  const [pendingFileCount, setPendingFileCount] = useState(0);

  const uploadFileAsyncRef = useRef(uploadFileAsync);
  const pollRef = useRef(pollAttachmentStatus);
  const setErrorsRef = useRef(setErrors);
  const setShowToastRef = useRef(setShowToast);

  useEffect(() => {
    uploadFileAsyncRef.current = uploadFileAsync;
  }, [uploadFileAsync]);
  useEffect(() => {
    pollRef.current = pollAttachmentStatus;
  }, [pollAttachmentStatus]);
  useEffect(() => {
    setErrorsRef.current = setErrors;
  }, [setErrors]);
  useEffect(() => {
    setShowToastRef.current = setShowToast;
  }, [setShowToast]);

  const syncPending = useCallback(() => {
    const n = countQueuedFiles(queueRef.current);
    setPendingFileCount(n);
    onPendingCountChange?.(n);
  }, [onPendingCountChange]);

  const clearQueue = useCallback(() => {
    queueRef.current = [];
    syncPending();
  }, [syncPending]);

  useEffect(() => {
    if (typeof onRegisterClearQueue === "function") {
      onRegisterClearQueue(clearQueue);
      return () => onRegisterClearQueue(null);
    }
    return undefined;
  }, [clearQueue, onRegisterClearQueue]);

  const kickDrainer = useCallback(() => {
    if (drainingRef.current) return;
    drainingRef.current = true;

    void (async () => {
      try {
        while (queueRef.current.length > 0) {
          const job = queueRef.current[0];
          while (job.idx < job.files.length) {
            const waveSize = Math.min(
              UPLOAD_BURST_CONCURRENCY,
              job.files.length - job.idx
            );
            const wave = job.files.slice(job.idx, job.idx + waveSize);
            await Promise.all(
              wave.map(async (file) => {
                try {
                  await uploadFileAsyncRef.current(file, job.reportId);
                } catch (err) {
                  console.error("[UploadJobs] File upload failed:", file?.name, err);
                  setErrorsRef.current?.((prev) => ({
                    ...prev,
                    general: `❌ Αποτυχία μεταφόρτωσης: ${file?.name || "αρχείο"}`,
                  }));
                  setShowToastRef.current?.(true);
                }
              })
            );
            job.idx += waveSize;
            syncPending();
          }

          const finished = queueRef.current.shift();
          syncPending();
          if (finished?.reportId != null) {
            pollRef.current?.(finished.reportId).catch((e) => {
              console.error("[UploadJobs] pollAttachmentStatus:", e);
            });
          }
        }
      } finally {
        drainingRef.current = false;
        if (queueRef.current.length > 0) {
          kickDrainer();
        }
      }
    })();
  }, [syncPending]);

  const enqueueUploadJob = useCallback(
    ({ reportId, files }) => {
      const list = Array.isArray(files) ? files.filter(Boolean) : [];
      if (!list.length || reportId == null || reportId === "") {
        return;
      }
      queueRef.current.push({
        reportId,
        files: [...list],
        idx: 0,
      });
      syncPending();
      kickDrainer();
    },
    [kickDrainer, syncPending]
  );

  useEffect(() => {
    if (typeof onRegisterEnqueue === "function") {
      onRegisterEnqueue(enqueueUploadJob);
      return () => onRegisterEnqueue(null);
    }
    return undefined;
  }, [enqueueUploadJob, onRegisterEnqueue]);

  const value = useMemo(
    () => ({
      enqueueUploadJob,
      pendingFileCount,
      clearQueue,
    }),
    [enqueueUploadJob, pendingFileCount, clearQueue]
  );

  return (
    <UploadJobsContext.Provider value={value}>{children}</UploadJobsContext.Provider>
  );
}

export function useUploadJobs() {
  const ctx = useContext(UploadJobsContext);
  if (!ctx) {
    return {
      enqueueUploadJob: () => {},
      pendingFileCount: 0,
      clearQueue: () => {},
    };
  }
  return ctx;
}

/** Slim footer row: queued files not yet finished uploading (XHR). */
export function UploadSyncFooter() {
  const { pendingFileCount } = useUploadJobs();
  if (pendingFileCount <= 0) return null;
  return (
    <div className="upload-sync-footer" role="status" aria-live="polite">
      <span className="upload-sync-footer__dot" aria-hidden />
      <span className="upload-sync-footer__label">Συγχρονισμός…</span>
      <span className="upload-sync-footer__count">
        {pendingFileCount}{" "}
        {pendingFileCount === 1 ? "αρχείο απομένει" : "αρχεία απομένουν"}
      </span>
    </div>
  );
}
